<template>
	<div class="GridFlex" :class="classes">
		<!-- @slot Only CloGridflexCell should go here -->
		<slot />
	</div>
</template>

<script>
import CloGridFlexCell from '@/grid/CloGridFlex/CloGridFlexCell';

export default {
	components: { CloGridFlexCell },
	name: 'CloGridFlex',
	data() {
		return {};
	},
	props: {
		maxWidth: {
			type: String,
			default: '',
		},
		behavior: {
			type: String,
			default: '',
		},
		alignment: {
			type: String,
			default: '',
		},
		verticalAlignment: {
			type: String,
			default: '',
		},
		gutter: {
			type: String,
			default: '',
		},
	},
	computed: {
		classes: function () {
			let classes = [];
			const prefix = 'GridFlex--';
			if (this.maxWidth) {
				classes.push(prefix + this.maxWidth);
			}

			if (this.behavior) {
				classes.push(prefix + this.behavior);
			}

			if (this.alignment) {
				classes.push(prefix + this.alignment);
			}

			if (this.verticalAlignment) {
				classes.push(prefix + this.verticalAlignment);
			}

			if (this.gutter) {
				switch (this.gutter) {
					case 'sm':
						classes.push(prefix + 'withGutterSmall');
						break;
					case 'md':
						classes.push(prefix + 'withGutterMedium');
						break;
					case 'lg':
						classes.push(prefix + 'withGutterLarge');
						break;
					case 'xl':
						classes.push(prefix + 'withGutterXlarge');
						break;
					case 'responsive':
						classes.push(prefix + 'withGutterResponsive');
						break;
					case 'responsiveLarge':
						classes.push(prefix + 'withGutterResponsiveLarge');
						break;
				}
			}
			return classes.join(' ');
		},
	},
};
</script>

<style lang="scss">
.GridFlex {
	display: flex;
	flex: 0 1 auto; // makes nestable
	flex-direction: row;
	flex-wrap: wrap;
	margin: 0;
	padding: 0;
}

// Variants for alternate max widths of GridFlex "row" container
.GridFlex--maxWidthWidthAuto {
	max-width: 102rem;
	margin: 0 auto;
}
.GridFlex--maxWidthXXLarge,
.GridFlex--xxl {
	max-width: map-get($breakpoints, xxlarge);
	margin: 0 auto;
}
.GridFlex--maxWidthXLarge,
.GridFlex--xl {
	max-width: map-get($breakpoints, xlarge);
	margin: 0 auto;
}
.GridFlex--maxWidthLarge,
.GridFlex--lg {
	max-width: map-get($breakpoints, large);
	margin: 0 auto;
}
.GridFlex--maxWidthMedium,
.GridFlex--md {
	max-width: map-get($breakpoints, medium);
	margin: 0 auto;
}

// The most basic cell with no breakpoint logic
.GridFlex-cell {
	flex-basis: 100%;
	min-width: 0;
}

// -------------------------------------------
// Positioning all cells from outer container (GridFlex)
// -------------------------------------------

// Restore default flex child behavior of cells
.GridFlex--flex {
	.GridFlex-cell {
		flex: 0 1 auto;
	}
}

// Fit cells to width of container
.GridFlex--fit {
	.GridFlex-cell {
		flex: 1 1 0%;
	}
}

// All child cells match height of the tallest
.GridFlex--equalHeight {
	.GridFlex-cell {
		display: flex;
	}
}

// Align cells to the horizontal center of container
.GridFlex--alignCenter {
	justify-content: center;
}

// Align cells to the horizontal end (right) of container
.GridFlex--alignRight {
	justify-content: flex-end;
}

// Align cells to the vertical middle of container
.GridFlex--alignMiddle {
	align-items: center;
}

// Align cells to the vertical end (bottom) of container
.GridFlex--alignBottom {
	align-items: flex-end;
}

// Align cells to the baseline of their text
.GridFlex--alignBaseline {
	align-items: baseline;
}

// Evenly distribute space around cells
.GridFlex--spaceAround {
	justify-content: space-around;
}

// Evenly distribute space around cells
.GridFlex--spaceBetween {
	justify-content: space-between;
}

// Add gutters between cells - standard (medium) 1rem
.GridFlex--withGutter,
.GridFlex--withGutterMedium {
	margin: 0 calc(-0.5 *#{$space-md});
	> .GridFlex-cell {
		padding: 0 calc(0.5 *#{$space-md});
	}
}
// Add gutters between cells - small 0.5rem
.GridFlex--withGutterSmall {
	margin: 0 calc(-0.5 *#{$space-sm});
	> .GridFlex-cell {
		padding: 0 calc(0.5 *#{$space-sm});
	}
}
// Add gutters between cells - large 2rem
.GridFlex--withGutterLarge {
	margin: 0 calc(-0.5 *#{$space-lg});
	> .GridFlex-cell {
		padding: 0 calc(0.5 *#{$space-lg});
	}
}
// Add gutters between cells - xlarge 4rem
.GridFlex--withGutterXlarge {
	margin: 0 calc(-0.5 *#{$space-xl});
	> .GridFlex-cell {
		padding: 0 calc(0.5 *#{$space-xl});
	}
}

// Add gutters between cells - responsive 1rem to 2rem
.GridFlex--withGutterResponsive {
	margin: 0 calc(-0.5 *#{$space-md});
	> .GridFlex-cell {
		padding: 0 calc(0.5 *#{$space-md});
	}
	@include breakpoint(large) {
		margin: 0 calc(-0.5 *#{$space-lg});
		> .GridFlex-cell {
			padding: 0 calc(0.5 *#{$space-lg});
		}
	}
}

// Add gutters between cells - responsive 1rem to 2rem to 4rem
.GridFlex--withGutterResponsiveLarge {
	margin: 0 calc(-0.5 *#{$space-md});
	> .GridFlex-cell {
		padding: 0 calc(0.5 *#{$space-md});
	}
	@include breakpoint(medium) {
		margin: 0 calc(-0.5 *#{$space-lg});
		> .GridFlex-cell {
			padding: 0 calc(0.5 *#{$space-lg});
		}
	}
	@include breakpoint(large) {
		margin: 0 calc(-0.5 *#{$space-xl});
		> .GridFlex-cell {
			padding: 0 calc(0.5 *#{$space-xl});
		}
	}
}

// Add small vertical divider at medium breakpoint
.GridFlex--gap-v > .GridFlex-cell {
	border-bottom: $space-xs solid $color-white;
	@include breakpoint(medium) {
		&:last-child {
			border-bottom: 0;
		}
	}
}

// Add small horizontal divider at medium breakpoint
.GridFlex--gap-h > .GridFlex-cell {
	@include breakpoint(medium) {
		border-left: $space-xs solid $color-white;
		&:first-child {
			border-left: 0;
		}
		[dir='rtl'] & {
			border-left: 0;
			border-right: $space-xs solid $color-white;
			&:first-child {
				border-right: 0;
			}
		}
	}
}

// -------------------------------------------
// Positioning individual cells
// -------------------------------------------

// Center contents of one cell
.GridFlex-cell--center {
	margin: 0 auto;
}
// Align cell to vertical middle
.GridFlex-cell--middle {
	align-self: center;
}
// Align cell to vertical bottom
.GridFlex-cell--bottom {
	align-self: flex-end;
}
// Reorder cell to be first in row
.GridFlex-cell--first {
	order: -1;
}
// Reorder cell to be last in row
.GridFlex-cell--last {
	order: 1;
}

// -------------------------------------------
// Sizes (no breakpoint)
// -------------------------------------------

.u-size1of12 {
	flex-basis: auto !important;
	width: 8.333% !important;
}

.u-size1of10 {
	flex-basis: auto !important;
	width: 10% !important;
}

.u-size1of8 {
	flex-basis: auto !important;
	width: 12.5% !important;
}

.u-size1of6,
.u-size2of12 {
	flex-basis: auto !important;
	width: 16.666% !important;
}

.u-size1of5,
.u-size2of10 {
	flex-basis: auto !important;
	width: 20% !important;
}

.u-size1of4,
.u-size2of8,
.u-size3of12 {
	flex-basis: auto !important;
	width: 25% !important;
}

.u-size3of10 {
	flex-basis: auto !important;
	width: 30% !important;
}

.u-size1of3,
.u-size2of6,
.u-size4of12 {
	flex-basis: auto !important;
	width: 33.333% !important;
}

.u-size3of8 {
	flex-basis: auto !important;
	width: 37.5% !important;
}

.u-size2of5,
.u-size4of10 {
	flex-basis: auto !important;
	width: 40% !important;
}

.u-size5of12 {
	flex-basis: auto !important;
	width: 41.666% !important;
}

.u-size1of2,
.u-size2of4,
.u-size3of6,
.u-size4of8,
.u-size5of10,
.u-size6of12 {
	flex-basis: auto !important;
	width: 50% !important;
}

.u-size7of12 {
	flex-basis: auto !important;
	width: 58.333% !important;
}

.u-size3of5,
.u-size6of10 {
	flex-basis: auto !important;
	width: 60% !important;
}

.u-size5of8 {
	flex-basis: auto !important;
	width: 62.5% !important;
}

.u-size2of3,
.u-size4of6,
.u-size8of12 {
	flex-basis: auto !important;
	width: 66.666% !important;
}

.u-size7of10 {
	flex-basis: auto !important;
	width: 70% !important;
}

.u-size3of4,
.u-size6of8,
.u-size9of12 {
	flex-basis: auto !important;
	width: 75% !important;
}

.u-size4of5,
.u-size8of10 {
	flex-basis: auto !important;
	width: 80% !important;
}

.u-size5of6,
.u-size10of12 {
	flex-basis: auto !important;
	width: 83.333% !important;
}

.u-size7of8 {
	flex-basis: auto !important;
	width: 87.5% !important;
}

.u-size9of10 {
	flex-basis: auto !important;
	width: 90% !important;
}

.u-size11of12 {
	flex-basis: auto !important;
	width: 91.666% !important;
}

// Make an element shrink wrap its content
.u-sizeFit {
	flex-basis: auto !important;
	width: auto !important;
}

// Make an element fill the remaining space
.u-sizeFill {
	flex: 1 1 0% !important;
	flex-basis: 0% !important;
	width: auto !important;
}

// Offsets
// -------
@mixin offsetMargin($percentage) {
	margin-left: $percentage !important;
	[dir='rtl'] & {
		margin-left: 0 !important;
		margin-right: $percentage !important;
	}
}

.u-offset1of12 {
	@include offsetMargin('8.333%');
}

.u-offset1of10 {
	@include offsetMargin('10%');
}

.u-offset1of8 {
	@include offsetMargin('12.5%');
}

.u-offset1of6,
.u-offset2of12 {
	@include offsetMargin('16.666%');
}

.u-offset1of5,
.u-offset2of10 {
	@include offsetMargin('20%');
}

.u-offset1of4,
.u-offset2of8,
.u-offset3of12 {
	@include offsetMargin('25%');
}

.u-offset3of10 {
	@include offsetMargin('30%');
}

.u-offset1of3,
.u-offset2of6,
.u-offset4of12 {
	@include offsetMargin('33.333%');
}

.u-offset3of8 {
	@include offsetMargin('37.5%');
}

.u-offset2of5,
.u-offset4of10 {
	@include offsetMargin('40%');
}

.u-offset5of12 {
	@include offsetMargin('41.666%');
}

.u-offset1of2,
.u-offset2of4,
.u-offset3of6,
.u-offset4of8,
.u-offset5of10,
.u-offset6of12 {
	@include offsetMargin('50%');
}

.u-offset7of12 {
	@include offsetMargin('58.333%');
}

.u-offset3of5,
.u-offset6of10 {
	@include offsetMargin('60%');
}

.u-offset5of8 {
	@include offsetMargin('62.5%');
}

.u-offset2of3,
.u-offset4of6,
.u-offset8of12 {
	@include offsetMargin('66.666%');
}

.u-offset7of10 {
	@include offsetMargin('70%');
}

.u-offset3of4,
.u-offset6of8,
.u-offset9of12 {
	@include offsetMargin('75%');
}

.u-offset4of5,
.u-offset8of10 {
	@include offsetMargin('80%');
}

.u-offset5of6,
.u-offset10of12 {
	@include offsetMargin('83.333%');
}

.u-offset7of8 {
	@include offsetMargin('87.5%');
}

.u-offset9of10 {
	@include offsetMargin('90%');
}

.u-offset11of12 {
	@include offsetMargin('91.666%');
}

// -------------------------------------------
// Sizes - Small breakpoint
// -------------------------------------------
@include breakpoint(small) {
	.u-sm-size1of12 {
		flex-basis: auto !important;
		width: 8.333% !important;
	}

	.u-sm-size1of10 {
		flex-basis: auto !important;
		width: 10% !important;
	}

	.u-sm-size1of8 {
		flex-basis: auto !important;
		width: 12.5% !important;
	}

	.u-sm-size1of6,
	.u-sm-size2of12 {
		flex-basis: auto !important;
		width: 16.666% !important;
	}

	.u-sm-size1of5,
	.u-sm-size2of10 {
		flex-basis: auto !important;
		width: 20% !important;
	}

	.u-sm-size1of4,
	.u-sm-size2of8,
	.u-sm-size3of12 {
		flex-basis: auto !important;
		width: 25% !important;
	}

	.u-sm-size3of10 {
		flex-basis: auto !important;
		width: 30% !important;
	}

	.u-sm-size1of3,
	.u-sm-size2of6,
	.u-sm-size4of12 {
		flex-basis: auto !important;
		width: 33.333% !important;
	}

	.u-sm-size3of8 {
		flex-basis: auto !important;
		width: 37.5% !important;
	}

	.u-sm-size2of5,
	.u-sm-size4of10 {
		flex-basis: auto !important;
		width: 40% !important;
	}

	.u-sm-size5of12 {
		flex-basis: auto !important;
		width: 41.666% !important;
	}

	.u-sm-size1of2,
	.u-sm-size2of4,
	.u-sm-size3of6,
	.u-sm-size4of8,
	.u-sm-size5of10,
	.u-sm-size6of12 {
		flex-basis: auto !important;
		width: 50% !important;
	}

	.u-sm-size7of12 {
		flex-basis: auto !important;
		width: 58.333% !important;
	}

	.u-sm-size3of5,
	.u-sm-size6of10 {
		flex-basis: auto !important;
		width: 60% !important;
	}

	.u-sm-size5of8 {
		flex-basis: auto !important;
		width: 62.5% !important;
	}

	.u-sm-size2of3,
	.u-sm-size4of6,
	.u-sm-size8of12 {
		flex-basis: auto !important;
		width: 66.666% !important;
	}

	.u-sm-size7of10 {
		flex-basis: auto !important;
		width: 70% !important;
	}

	.u-sm-size3of4,
	.u-sm-size6of8,
	.u-sm-size9of12 {
		flex-basis: auto !important;
		width: 75% !important;
	}

	.u-sm-size4of5,
	.u-sm-size8of10 {
		flex-basis: auto !important;
		width: 80% !important;
	}

	.u-sm-size5of6,
	.u-sm-size10of12 {
		flex-basis: auto !important;
		width: 83.333% !important;
	}

	.u-sm-size7of8 {
		flex-basis: auto !important;
		width: 87.5% !important;
	}

	.u-sm-size9of10 {
		flex-basis: auto !important;
		width: 90% !important;
	}

	.u-sm-size11of12 {
		flex-basis: auto !important;
		width: 91.666% !important;
	}

	// Make an element shrink wrap its content
	.u-sm-sizeFit {
		flex-basis: auto !important;
		width: auto !important;
	}

	// Make an element fill the remaining space
	.u-sm-sizeFill {
		flex: 1 1 0% !important;
		flex-basis: 0% !important;
		width: auto !important;
	}

	// Offsets
	// -------

	.u-sm-offset1of12 {
		@include offsetMargin('8.333%');
	}

	.u-sm-offset1of10 {
		@include offsetMargin('10%');
	}

	.u-sm-offset1of8 {
		@include offsetMargin('12.5%');
	}

	.u-sm-offset1of6,
	.u-sm-offset2of12 {
		@include offsetMargin('16.666%');
	}

	.u-sm-offset1of5,
	.u-sm-offset2of10 {
		@include offsetMargin('20%');
	}

	.u-sm-offset1of4,
	.u-sm-offset2of8,
	.u-sm-offset3of12 {
		@include offsetMargin('25%');
	}

	.u-sm-offset3of10 {
		@include offsetMargin('30%');
	}

	.u-sm-offset1of3,
	.u-sm-offset2of6,
	.u-sm-offset4of12 {
		@include offsetMargin('33.333%');
	}

	.u-sm-offset3of8 {
		@include offsetMargin('37.5%');
	}

	.u-sm-offset2of5,
	.u-sm-offset4of10 {
		@include offsetMargin('40%');
	}

	.u-sm-offset5of12 {
		@include offsetMargin('41.666%');
	}

	.u-sm-offset1of2,
	.u-sm-offset2of4,
	.u-sm-offset3of6,
	.u-sm-offset4of8,
	.u-sm-offset5of10,
	.u-sm-offset6of12 {
		@include offsetMargin('50%');
	}

	.u-sm-offset7of12 {
		@include offsetMargin('58.333%');
	}

	.u-sm-offset3of5,
	.u-sm-offset6of10 {
		@include offsetMargin('60%');
	}

	.u-sm-offset5of8 {
		@include offsetMargin('62.5%');
	}

	.u-sm-offset2of3,
	.u-sm-offset4of6,
	.u-sm-offset8of12 {
		@include offsetMargin('66.666%');
	}

	.u-sm-offset7of10 {
		@include offsetMargin('70%');
	}

	.u-sm-offset3of4,
	.u-sm-offset6of8,
	.u-sm-offset9of12 {
		@include offsetMargin('75%');
	}

	.u-sm-offset4of5,
	.u-sm-offset8of10 {
		@include offsetMargin('80%');
	}

	.u-sm-offset5of6,
	.u-sm-offset10of12 {
		@include offsetMargin('83.333%');
	}

	.u-sm-offset7of8 {
		@include offsetMargin('87.5%');
	}

	.u-sm-offset9of10 {
		@include offsetMargin('90%');
	}

	.u-sm-offset11of12 {
		@include offsetMargin('91.666%');
	}

	// -------------------------------------------
	// Positioning individual cells
	// -------------------------------------------

	// Reorder cell to be first in row
	.GridFlex-cell-sm--first {
		order: -1;
	}
	// Reorder cell to be last in row
	.GridFlex-cell-sm--last {
		order: 1;
	}
}

// -------------------------------------------
// Sizes: Medium-small breakpoint
// -------------------------------------------
@include breakpoint(mediumsmall) {
	.u-ms-size1of12 {
		flex-basis: auto !important;
		width: 8.333% !important;
	}

	.u-ms-size1of10 {
		flex-basis: auto !important;
		width: 10% !important;
	}

	.u-ms-size1of8 {
		flex-basis: auto !important;
		width: 12.5% !important;
	}

	.u-ms-size1of6,
	.u-ms-size2of12 {
		flex-basis: auto !important;
		width: 16.666% !important;
	}

	.u-ms-size1of5,
	.u-ms-size2of10 {
		flex-basis: auto !important;
		width: 20% !important;
	}

	.u-ms-size1of4,
	.u-ms-size2of8,
	.u-ms-size3of12 {
		flex-basis: auto !important;
		width: 25% !important;
	}

	.u-ms-size3of10 {
		flex-basis: auto !important;
		width: 30% !important;
	}

	.u-ms-size1of3,
	.u-ms-size2of6,
	.u-ms-size4of12 {
		flex-basis: auto !important;
		width: 33.333% !important;
	}

	.u-ms-size3of8 {
		flex-basis: auto !important;
		width: 37.5% !important;
	}

	.u-ms-size2of5,
	.u-ms-size4of10 {
		flex-basis: auto !important;
		width: 40% !important;
	}

	.u-ms-size5of12 {
		flex-basis: auto !important;
		width: 41.666% !important;
	}

	.u-ms-size1of2,
	.u-ms-size2of4,
	.u-ms-size3of6,
	.u-ms-size4of8,
	.u-ms-size5of10,
	.u-ms-size6of12 {
		flex-basis: auto !important;
		width: 50% !important;
	}

	.u-ms-size7of12 {
		flex-basis: auto !important;
		width: 58.333% !important;
	}

	.u-ms-size3of5,
	.u-ms-size6of10 {
		flex-basis: auto !important;
		width: 60% !important;
	}

	.u-ms-size5of8 {
		flex-basis: auto !important;
		width: 62.5% !important;
	}

	.u-ms-size2of3,
	.u-ms-size4of6,
	.u-ms-size8of12 {
		flex-basis: auto !important;
		width: 66.666% !important;
	}

	.u-ms-size7of10 {
		flex-basis: auto !important;
		width: 70% !important;
	}

	.u-ms-size3of4,
	.u-ms-size6of8,
	.u-ms-size9of12 {
		flex-basis: auto !important;
		width: 75% !important;
	}

	.u-ms-size4of5,
	.u-ms-size8of10 {
		flex-basis: auto !important;
		width: 80% !important;
	}

	.u-ms-size5of6,
	.u-ms-size10of12 {
		flex-basis: auto !important;
		width: 83.333% !important;
	}

	.u-ms-size7of8 {
		flex-basis: auto !important;
		width: 87.5% !important;
	}

	.u-ms-size9of10 {
		flex-basis: auto !important;
		width: 90% !important;
	}

	.u-ms-size11of12 {
		flex-basis: auto !important;
		width: 91.666% !important;
	}

	// Make an element shrink wrap its content
	.u-ms-sizeFit {
		flex-basis: auto !important;
		width: auto !important;
	}

	// Make an element fill the remaining space
	.u-ms-sizeFill {
		flex: 1 1 0% !important;
		flex-basis: 0% !important;
		width: auto !important;
	}

	// Offsets
	// -------

	.u-ms-offset1of12 {
		@include offsetMargin('8.333%');
	}

	.u-ms-offset1of10 {
		@include offsetMargin('10%');
	}

	.u-ms-offset1of8 {
		@include offsetMargin('12.5%');
	}

	.u-ms-offset1of6,
	.u-ms-offset2of12 {
		@include offsetMargin('16.666%');
	}

	.u-ms-offset1of5,
	.u-ms-offset2of10 {
		@include offsetMargin('20%');
	}

	.u-ms-offset1of4,
	.u-ms-offset2of8,
	.u-ms-offset3of12 {
		@include offsetMargin('25%');
	}

	.u-ms-offset3of10 {
		@include offsetMargin('30%');
	}

	.u-ms-offset1of3,
	.u-ms-offset2of6,
	.u-ms-offset4of12 {
		@include offsetMargin('33.333%');
	}

	.u-ms-offset3of8 {
		@include offsetMargin('37.5%');
	}

	.u-ms-offset2of5,
	.u-ms-offset4of10 {
		@include offsetMargin('40%');
	}

	.u-ms-offset5of12 {
		@include offsetMargin('41.666%');
	}

	.u-ms-offset1of2,
	.u-ms-offset2of4,
	.u-ms-offset3of6,
	.u-ms-offset4of8,
	.u-ms-offset5of10,
	.u-ms-offset6of12 {
		@include offsetMargin('50%');
	}

	.u-ms-offset7of12 {
		@include offsetMargin('58.333%');
	}

	.u-ms-offset3of5,
	.u-ms-offset6of10 {
		@include offsetMargin('60%');
	}

	.u-ms-offset5of8 {
		@include offsetMargin('62.5%');
	}

	.u-ms-offset2of3,
	.u-ms-offset4of6,
	.u-ms-offset8of12 {
		@include offsetMargin('66.666%');
	}

	.u-ms-offset7of10 {
		@include offsetMargin('70%');
	}

	.u-ms-offset3of4,
	.u-ms-offset6of8,
	.u-ms-offset9of12 {
		@include offsetMargin('75%');
	}

	.u-ms-offset4of5,
	.u-ms-offset8of10 {
		@include offsetMargin('80%');
	}

	.u-ms-offset5of6,
	.u-ms-offset10of12 {
		@include offsetMargin('83.333%');
	}

	.u-ms-offset7of8 {
		@include offsetMargin('87.5%');
	}

	.u-ms-offset9of10 {
		@include offsetMargin('90%');
	}

	.u-ms-offset11of12 {
		@include offsetMargin('91.666%');
	}

	// -------------------------------------------
	// Positioning individual cells
	// -------------------------------------------

	// Reorder cell to be first in row
	.GridFlex-cell-ms--first {
		order: -1;
	}
	// Reorder cell to be last in row
	.GridFlex-cell-ms--last {
		order: 1;
	}
}

// -------------------------------------------
// Sizes: Medium breakpoint
// -------------------------------------------
@include breakpoint(medium) {
	.u-md-size1of12 {
		flex-basis: auto !important;
		width: 8.333% !important;
	}

	.u-md-size1of10 {
		flex-basis: auto !important;
		width: 10% !important;
	}

	.u-md-size1of8 {
		flex-basis: auto !important;
		width: 12.5% !important;
	}

	.u-md-size1of6,
	.u-md-size2of12 {
		flex-basis: auto !important;
		width: 16.666% !important;
	}

	.u-md-size1of5,
	.u-md-size2of10 {
		flex-basis: auto !important;
		width: 20% !important;
	}

	.u-md-size1of4,
	.u-md-size2of8,
	.u-md-size3of12 {
		flex-basis: auto !important;
		width: 25% !important;
	}

	.u-md-size3of10 {
		flex-basis: auto !important;
		width: 30% !important;
	}

	.u-md-size1of3,
	.u-md-size2of6,
	.u-md-size4of12 {
		flex-basis: auto !important;
		width: 33.333% !important;
	}

	.u-md-size3of8 {
		flex-basis: auto !important;
		width: 37.5% !important;
	}

	.u-md-size2of5,
	.u-md-size4of10 {
		flex-basis: auto !important;
		width: 40% !important;
	}

	.u-md-size5of12 {
		flex-basis: auto !important;
		width: 41.666% !important;
	}

	.u-md-size1of2,
	.u-md-size2of4,
	.u-md-size3of6,
	.u-md-size4of8,
	.u-md-size5of10,
	.u-md-size6of12 {
		flex-basis: auto !important;
		width: 50% !important;
	}

	.u-md-size7of12 {
		flex-basis: auto !important;
		width: 58.333% !important;
	}

	.u-md-size3of5,
	.u-md-size6of10 {
		flex-basis: auto !important;
		width: 60% !important;
	}

	.u-md-size5of8 {
		flex-basis: auto !important;
		width: 62.5% !important;
	}

	.u-md-size2of3,
	.u-md-size4of6,
	.u-md-size8of12 {
		flex-basis: auto !important;
		width: 66.666% !important;
	}

	.u-md-size7of10 {
		flex-basis: auto !important;
		width: 70% !important;
	}

	.u-md-size3of4,
	.u-md-size6of8,
	.u-md-size9of12 {
		flex-basis: auto !important;
		width: 75% !important;
	}

	.u-md-size4of5,
	.u-md-size8of10 {
		flex-basis: auto !important;
		width: 80% !important;
	}

	.u-md-size5of6,
	.u-md-size10of12 {
		flex-basis: auto !important;
		width: 83.333% !important;
	}

	.u-md-size7of8 {
		flex-basis: auto !important;
		width: 87.5% !important;
	}

	.u-md-size9of10 {
		flex-basis: auto !important;
		width: 90% !important;
	}

	.u-md-size11of12 {
		flex-basis: auto !important;
		width: 91.666% !important;
	}

	// Make an element shrink wrap its content
	.u-md-sizeFit {
		flex-basis: auto !important;
		width: auto !important;
	}

	// Make an element fill the remaining space
	.u-md-sizeFill {
		flex: 1 1 0% !important;
		flex-basis: 0% !important;
		width: auto !important;
	}

	// Offsets
	// -------

	.u-md-offset1of12 {
		@include offsetMargin('8.333%');
	}

	.u-md-offset1of10 {
		@include offsetMargin('10%');
	}

	.u-md-offset1of8 {
		@include offsetMargin('12.5%');
	}

	.u-md-offset1of6,
	.u-md-offset2of12 {
		@include offsetMargin('16.666%');
	}

	.u-md-offset1of5,
	.u-md-offset2of10 {
		@include offsetMargin('20%');
	}

	.u-md-offset1of4,
	.u-md-offset2of8,
	.u-md-offset3of12 {
		@include offsetMargin('25%');
	}

	.u-md-offset3of10 {
		@include offsetMargin('30%');
	}

	.u-md-offset1of3,
	.u-md-offset2of6,
	.u-md-offset4of12 {
		@include offsetMargin('33.333%');
	}

	.u-md-offset3of8 {
		@include offsetMargin('37.5%');
	}

	.u-md-offset2of5,
	.u-md-offset4of10 {
		@include offsetMargin('40%');
	}

	.u-md-offset5of12 {
		@include offsetMargin('41.666%');
	}

	.u-md-offset1of2,
	.u-md-offset2of4,
	.u-md-offset3of6,
	.u-md-offset4of8,
	.u-md-offset5of10,
	.u-md-offset6of12 {
		@include offsetMargin('50%');
	}

	.u-md-offset7of12 {
		@include offsetMargin('58.333%');
	}

	.u-md-offset3of5,
	.u-md-offset6of10 {
		@include offsetMargin('60%');
	}

	.u-md-offset5of8 {
		@include offsetMargin('62.5%');
	}

	.u-md-offset2of3,
	.u-md-offset4of6,
	.u-md-offset8of12 {
		@include offsetMargin('66.666%');
	}

	.u-md-offset7of10 {
		@include offsetMargin('70%');
	}

	.u-md-offset3of4,
	.u-md-offset6of8,
	.u-md-offset9of12 {
		@include offsetMargin('75%');
	}

	.u-md-offset4of5,
	.u-md-offset8of10 {
		@include offsetMargin('80%');
	}

	.u-md-offset5of6,
	.u-md-offset10of12 {
		@include offsetMargin('83.333%');
	}

	.u-md-offset7of8 {
		@include offsetMargin('87.5%');
	}

	.u-md-offset9of10 {
		@include offsetMargin('90%');
	}

	.u-md-offset11of12 {
		@include offsetMargin('91.666%');
	}

	// -------------------------------------------
	// Positioning individual cells
	// -------------------------------------------

	// Reorder cell to be first in row
	.GridFlex-cell-md--first {
		order: -1;
	}
	// Reorder cell to be last in row
	.GridFlex-cell-md--last {
		order: 1;
	}
}

// -------------------------------------------
// Sizes: Large breakpoint
// -------------------------------------------
@include breakpoint(large) {
	.u-lg-size1of12 {
		flex-basis: auto !important;
		width: 8.333% !important;
	}

	.u-lg-size1of10 {
		flex-basis: auto !important;
		width: 10% !important;
	}

	.u-lg-size1of8 {
		flex-basis: auto !important;
		width: 12.5% !important;
	}

	.u-lg-size1of6,
	.u-lg-size2of12 {
		flex-basis: auto !important;
		width: 16.666% !important;
	}

	.u-lg-size1of5,
	.u-lg-size2of10 {
		flex-basis: auto !important;
		width: 20% !important;
	}

	.u-lg-size1of4,
	.u-lg-size2of8,
	.u-lg-size3of12 {
		flex-basis: auto !important;
		width: 25% !important;
	}

	.u-lg-size3of10 {
		flex-basis: auto !important;
		width: 30% !important;
	}

	.u-lg-size1of3,
	.u-lg-size2of6,
	.u-lg-size4of12 {
		flex-basis: auto !important;
		width: 33.333% !important;
	}

	.u-lg-size3of8 {
		flex-basis: auto !important;
		width: 37.5% !important;
	}

	.u-lg-size2of5,
	.u-lg-size4of10 {
		flex-basis: auto !important;
		width: 40% !important;
	}

	.u-lg-size5of12 {
		flex-basis: auto !important;
		width: 41.666% !important;
	}

	.u-lg-size1of2,
	.u-lg-size2of4,
	.u-lg-size3of6,
	.u-lg-size4of8,
	.u-lg-size5of10,
	.u-lg-size6of12 {
		flex-basis: auto !important;
		width: 50% !important;
	}

	.u-lg-size7of12 {
		flex-basis: auto !important;
		width: 58.333% !important;
	}

	.u-lg-size3of5,
	.u-lg-size6of10 {
		flex-basis: auto !important;
		width: 60% !important;
	}

	.u-lg-size5of8 {
		flex-basis: auto !important;
		width: 62.5% !important;
	}

	.u-lg-size2of3,
	.u-lg-size4of6,
	.u-lg-size8of12 {
		flex-basis: auto !important;
		width: 66.666% !important;
	}

	.u-lg-size7of10 {
		flex-basis: auto !important;
		width: 70% !important;
	}

	.u-lg-size3of4,
	.u-lg-size6of8,
	.u-lg-size9of12 {
		flex-basis: auto !important;
		width: 75% !important;
	}

	.u-lg-size4of5,
	.u-lg-size8of10 {
		flex-basis: auto !important;
		width: 80% !important;
	}

	.u-lg-size5of6,
	.u-lg-size10of12 {
		flex-basis: auto !important;
		width: 83.333% !important;
	}

	.u-lg-size7of8 {
		flex-basis: auto !important;
		width: 87.5% !important;
	}

	.u-lg-size9of10 {
		flex-basis: auto !important;
		width: 90% !important;
	}

	.u-lg-size11of12 {
		flex-basis: auto !important;
		width: 91.666% !important;
	}

	// Make an element shrink wrap its content
	.u-lg-sizeFit {
		flex-basis: auto !important;
		width: auto !important;
	}

	// Make an element fill the remaining space
	.u-lg-sizeFill {
		flex: 1 1 0% !important;
		flex-basis: 0% !important;
		width: auto !important;
	}

	// Offsets
	// -------

	.u-lg-offset1of12 {
		@include offsetMargin('8.333%');
	}

	.u-lg-offset1of10 {
		@include offsetMargin('10%');
	}

	.u-lg-offset1of8 {
		@include offsetMargin('12.5%');
	}

	.u-lg-offset1of6,
	.u-lg-offset2of12 {
		@include offsetMargin('16.666%');
	}

	.u-lg-offset1of5,
	.u-lg-offset2of10 {
		@include offsetMargin('20%');
	}

	.u-lg-offset1of4,
	.u-lg-offset2of8,
	.u-lg-offset3of12 {
		@include offsetMargin('25%');
	}

	.u-lg-offset3of10 {
		@include offsetMargin('30%');
	}

	.u-lg-offset1of3,
	.u-lg-offset2of6,
	.u-lg-offset4of12 {
		@include offsetMargin('33.333%');
	}

	.u-lg-offset3of8 {
		@include offsetMargin('37.5%');
	}

	.u-lg-offset2of5,
	.u-lg-offset4of10 {
		@include offsetMargin('40%');
	}

	.u-lg-offset5of12 {
		@include offsetMargin('41.666%');
	}

	.u-lg-offset1of2,
	.u-lg-offset2of4,
	.u-lg-offset3of6,
	.u-lg-offset4of8,
	.u-lg-offset5of10,
	.u-lg-offset6of12 {
		@include offsetMargin('50%');
	}

	.u-lg-offset7of12 {
		@include offsetMargin('58.333%');
	}

	.u-lg-offset3of5,
	.u-lg-offset6of10 {
		@include offsetMargin('60%');
	}

	.u-lg-offset5of8 {
		@include offsetMargin('62.5%');
	}

	.u-lg-offset2of3,
	.u-lg-offset4of6,
	.u-lg-offset8of12 {
		@include offsetMargin('66.666%');
	}

	.u-lg-offset7of10 {
		@include offsetMargin('70%');
	}

	.u-lg-offset3of4,
	.u-lg-offset6of8,
	.u-lg-offset9of12 {
		@include offsetMargin('75%');
	}

	.u-lg-offset4of5,
	.u-lg-offset8of10 {
		@include offsetMargin('80%');
	}

	.u-lg-offset5of6,
	.u-lg-offset10of12 {
		@include offsetMargin('83.333%');
	}

	.u-lg-offset7of8 {
		@include offsetMargin('87.5%');
	}

	.u-lg-offset9of10 {
		@include offsetMargin('90%');
	}

	.u-lg-offset11of12 {
		@include offsetMargin('91.666%');
	}

	// -------------------------------------------
	// Positioning individual cells
	// -------------------------------------------

	// Reorder cell to be first in row
	.GridFlex-cell-lg--first {
		order: -1;
	}
	// Reorder cell to be last in row
	.GridFlex-cell-lg--last {
		order: 1;
	}
}

// -------------------------------------------
// Sizes: Extra-large breakpoint
// -------------------------------------------
@include breakpoint(xlarge) {
	.u-xl-size1of12 {
		flex-basis: auto !important;
		width: 8.333% !important;
	}

	.u-xl-size1of10 {
		flex-basis: auto !important;
		width: 10% !important;
	}

	.u-xl-size1of8 {
		flex-basis: auto !important;
		width: 12.5% !important;
	}

	.u-xl-size1of6,
	.u-xl-size2of12 {
		flex-basis: auto !important;
		width: 16.666% !important;
	}

	.u-xl-size1of5,
	.u-xl-size2of10 {
		flex-basis: auto !important;
		width: 20% !important;
	}

	.u-xl-size1of4,
	.u-xl-size2of8,
	.u-xl-size3of12 {
		flex-basis: auto !important;
		width: 25% !important;
	}

	.u-xl-size3of10 {
		flex-basis: auto !important;
		width: 30% !important;
	}

	.u-xl-size1of3,
	.u-xl-size2of6,
	.u-xl-size4of12 {
		flex-basis: auto !important;
		width: 33.333% !important;
	}

	.u-xl-size3of8 {
		flex-basis: auto !important;
		width: 37.5% !important;
	}

	.u-xl-size2of5,
	.u-xl-size4of10 {
		flex-basis: auto !important;
		width: 40% !important;
	}

	.u-xl-size5of12 {
		flex-basis: auto !important;
		width: 41.666% !important;
	}

	.u-xl-size1of2,
	.u-xl-size2of4,
	.u-xl-size3of6,
	.u-xl-size4of8,
	.u-xl-size5of10,
	.u-xl-size6of12 {
		flex-basis: auto !important;
		width: 50% !important;
	}

	.u-xl-size7of12 {
		flex-basis: auto !important;
		width: 58.333% !important;
	}

	.u-xl-size3of5,
	.u-xl-size6of10 {
		flex-basis: auto !important;
		width: 60% !important;
	}

	.u-xl-size5of8 {
		flex-basis: auto !important;
		width: 62.5% !important;
	}

	.u-xl-size2of3,
	.u-xl-size4of6,
	.u-xl-size8of12 {
		flex-basis: auto !important;
		width: 66.666% !important;
	}

	.u-xl-size7of10 {
		flex-basis: auto !important;
		width: 70% !important;
	}

	.u-xl-size3of4,
	.u-xl-size6of8,
	.u-xl-size9of12 {
		flex-basis: auto !important;
		width: 75% !important;
	}

	.u-xl-size4of5,
	.u-xl-size8of10 {
		flex-basis: auto !important;
		width: 80% !important;
	}

	.u-xl-size5of6,
	.u-xl-size10of12 {
		flex-basis: auto !important;
		width: 83.333% !important;
	}

	.u-xl-size7of8 {
		flex-basis: auto !important;
		width: 87.5% !important;
	}

	.u-xl-size9of10 {
		flex-basis: auto !important;
		width: 90% !important;
	}

	.u-xl-size11of12 {
		flex-basis: auto !important;
		width: 91.666% !important;
	}

	// Make an element shrink wrap its content
	.u-xl-sizeFit {
		flex-basis: auto !important;
		width: auto !important;
	}

	// Make an element fill the remaining space
	.u-xl-sizeFill {
		flex: 1 1 0% !important;
		flex-basis: 0% !important;
		width: auto !important;
	}

	// Offsets
	// -------

	.u-xl-offset1of12 {
		@include offsetMargin('8.333%');
	}

	.u-xl-offset1of10 {
		@include offsetMargin('10%');
	}

	.u-xl-offset1of8 {
		@include offsetMargin('12.5%');
	}

	.u-xl-offset1of6,
	.u-xl-offset2of12 {
		@include offsetMargin('16.666%');
	}

	.u-xl-offset1of5,
	.u-xl-offset2of10 {
		@include offsetMargin('20%');
	}

	.u-xl-offset1of4,
	.u-xl-offset2of8,
	.u-xl-offset3of12 {
		@include offsetMargin('25%');
	}

	.u-xl-offset3of10 {
		@include offsetMargin('30%');
	}

	.u-xl-offset1of3,
	.u-xl-offset2of6,
	.u-xl-offset4of12 {
		@include offsetMargin('33.333%');
	}

	.u-xl-offset3of8 {
		@include offsetMargin('37.5%');
	}

	.u-xl-offset2of5,
	.u-xl-offset4of10 {
		@include offsetMargin('40%');
	}

	.u-xl-offset5of12 {
		@include offsetMargin('41.666%');
	}

	.u-xl-offset1of2,
	.u-xl-offset2of4,
	.u-xl-offset3of6,
	.u-xl-offset4of8,
	.u-xl-offset5of10,
	.u-xl-offset6of12 {
		@include offsetMargin('50%');
	}

	.u-xl-offset7of12 {
		@include offsetMargin('58.333%');
	}

	.u-xl-offset3of5,
	.u-xl-offset6of10 {
		@include offsetMargin('60%');
	}

	.u-xl-offset5of8 {
		@include offsetMargin('62.5%');
	}

	.u-xl-offset2of3,
	.u-xl-offset4of6,
	.u-xl-offset8of12 {
		@include offsetMargin('66.666%');
	}

	.u-xl-offset7of10 {
		@include offsetMargin('70%');
	}

	.u-xl-offset3of4,
	.u-xl-offset6of8,
	.u-xl-offset9of12 {
		@include offsetMargin('75%');
	}

	.u-xl-offset4of5,
	.u-xl-offset8of10 {
		@include offsetMargin('80%');
	}

	.u-xl-offset5of6,
	.u-xl-offset10of12 {
		@include offsetMargin('83.333%');
	}

	.u-xl-offset7of8 {
		@include offsetMargin('87.5%');
	}

	.u-xl-offset9of10 {
		@include offsetMargin('90%');
	}

	.u-xl-offset11of12 {
		@include offsetMargin('91.666%');
	}

	// -------------------------------------------
	// Positioning individual cells
	// -------------------------------------------

	// Reorder cell to be first in row
	.GridFlex-cell-xl--first {
		order: -1;
	}
	// Reorder cell to be last in row
	.GridFlex-cell-xl--last {
		order: 1;
	}
}

// -------------------------------------------
// Sizes: Extra-extra-large breakpoint
// -------------------------------------------
@include breakpoint(xxlarge) {
	.u-xxl-size1of12 {
		flex-basis: auto !important;
		width: 8.333% !important;
	}

	.u-xxl-size1of10 {
		flex-basis: auto !important;
		width: 10% !important;
	}

	.u-xxl-size1of8 {
		flex-basis: auto !important;
		width: 12.5% !important;
	}

	.u-xxl-size1of6,
	.u-xxl-size2of12 {
		flex-basis: auto !important;
		width: 16.666% !important;
	}

	.u-xxl-size1of5,
	.u-xxl-size2of10 {
		flex-basis: auto !important;
		width: 20% !important;
	}

	.u-xxl-size1of4,
	.u-xxl-size2of8,
	.u-xxl-size3of12 {
		flex-basis: auto !important;
		width: 25% !important;
	}

	.u-xxl-size3of10 {
		flex-basis: auto !important;
		width: 30% !important;
	}

	.u-xxl-size1of3,
	.u-xxl-size2of6,
	.u-xxl-size4of12 {
		flex-basis: auto !important;
		width: 33.333% !important;
	}

	.u-xxl-size3of8 {
		flex-basis: auto !important;
		width: 37.5% !important;
	}

	.u-xxl-size2of5,
	.u-xxl-size4of10 {
		flex-basis: auto !important;
		width: 40% !important;
	}

	.u-xxl-size5of12 {
		flex-basis: auto !important;
		width: 41.666% !important;
	}

	.u-xxl-size1of2,
	.u-xxl-size2of4,
	.u-xxl-size3of6,
	.u-xxl-size4of8,
	.u-xxl-size5of10,
	.u-xxl-size6of12 {
		flex-basis: auto !important;
		width: 50% !important;
	}

	.u-xxl-size7of12 {
		flex-basis: auto !important;
		width: 58.333% !important;
	}

	.u-xxl-size3of5,
	.u-xxl-size6of10 {
		flex-basis: auto !important;
		width: 60% !important;
	}

	.u-xxl-size5of8 {
		flex-basis: auto !important;
		width: 62.5% !important;
	}

	.u-xxl-size2of3,
	.u-xxl-size4of6,
	.u-xxl-size8of12 {
		flex-basis: auto !important;
		width: 66.666% !important;
	}

	.u-xxl-size7of10 {
		flex-basis: auto !important;
		width: 70% !important;
	}

	.u-xxl-size3of4,
	.u-xxl-size6of8,
	.u-xxl-size9of12 {
		flex-basis: auto !important;
		width: 75% !important;
	}

	.u-xxl-size4of5,
	.u-xxl-size8of10 {
		flex-basis: auto !important;
		width: 80% !important;
	}

	.u-xxl-size5of6,
	.u-xxl-size10of12 {
		flex-basis: auto !important;
		width: 83.333% !important;
	}

	.u-xxl-size7of8 {
		flex-basis: auto !important;
		width: 87.5% !important;
	}

	.u-xxl-size9of10 {
		flex-basis: auto !important;
		width: 90% !important;
	}

	.u-xxl-size11of12 {
		flex-basis: auto !important;
		width: 91.666% !important;
	}

	// Make an element shrink wrap its content
	.u-xxl-sizeFit {
		flex-basis: auto !important;
		width: auto !important;
	}

	// Make an element fill the remaining space
	.u-xxl-sizeFill {
		flex: 1 1 0% !important;
		flex-basis: 0% !important;
		width: auto !important;
	}

	// Offsets
	// -------

	.u-xxl-offset1of12 {
		@include offsetMargin('8.333%');
	}

	.u-xxl-offset1of10 {
		@include offsetMargin('10%');
	}

	.u-xxl-offset1of8 {
		@include offsetMargin('12.5%');
	}

	.u-xxl-offset1of6,
	.u-xxl-offset2of12 {
		@include offsetMargin('16.666%');
	}

	.u-xxl-offset1of5,
	.u-xxl-offset2of10 {
		@include offsetMargin('20%');
	}

	.u-xxl-offset1of4,
	.u-xxl-offset2of8,
	.u-xxl-offset3of12 {
		@include offsetMargin('25%');
	}

	.u-xxl-offset3of10 {
		@include offsetMargin('30%');
	}

	.u-xxl-offset1of3,
	.u-xxl-offset2of6,
	.u-xxl-offset4of12 {
		@include offsetMargin('33.333%');
	}

	.u-xxl-offset3of8 {
		@include offsetMargin('37.5%');
	}

	.u-xxl-offset2of5,
	.u-xxl-offset4of10 {
		@include offsetMargin('40%');
	}

	.u-xxl-offset5of12 {
		@include offsetMargin('41.666%');
	}

	.u-xxl-offset1of2,
	.u-xxl-offset2of4,
	.u-xxl-offset3of6,
	.u-xxl-offset4of8,
	.u-xxl-offset5of10,
	.u-xxl-offset6of12 {
		@include offsetMargin('50%');
	}

	.u-xxl-offset7of12 {
		@include offsetMargin('58.333%');
	}

	.u-xxl-offset3of5,
	.u-xxl-offset6of10 {
		@include offsetMargin('60%');
	}

	.u-xxl-offset5of8 {
		@include offsetMargin('62.5%');
	}

	.u-xxl-offset2of3,
	.u-xxl-offset4of6,
	.u-xxl-offset8of12 {
		@include offsetMargin('66.666%');
	}

	.u-xxl-offset7of10 {
		@include offsetMargin('70%');
	}

	.u-xxl-offset3of4,
	.u-xxl-offset6of8,
	.u-xxl-offset9of12 {
		@include offsetMargin('75%');
	}

	.u-xxl-offset4of5,
	.u-xxl-offset8of10 {
		@include offsetMargin('80%');
	}

	.u-xxl-offset5of6,
	.u-xxl-offset10of12 {
		@include offsetMargin('83.333%');
	}

	.u-xxl-offset7of8 {
		@include offsetMargin('87.5%');
	}

	.u-xxl-offset9of10 {
		@include offsetMargin('90%');
	}

	.u-xxl-offset11of12 {
		@include offsetMargin('91.666%');
	}

	// -------------------------------------------
	// Positioning individual cells
	// -------------------------------------------

	// Reorder cell to be first in row
	.GridFlex-cell-xxl--first {
		order: -1;
	}
	// Reorder cell to be last in row
	.GridFlex-cell-xxl--last {
		order: 1;
	}
}
</style>
